import { For, JSX, Accessor, Signal } from 'solid-js';
import { SortableContext, SortableContextJsx } from './SortableContext';
import Item from './fragments/Item';
import styles from './st-sortable.module.css';

export class SortableProps<T> {
    items!: Signal<T[]>;                                                // Signal tuple: [getter, setter]
    itemKey?: (item: T) => string | number;
    onReorder?: (items: T[], fromIndex: number, toIndex: number) => void;
    disabled?: boolean = false;
    animation?: boolean = true;
    animationDuration?: number = 200;
    accepts?: string[];                                                 // Optional filter for what can be dropped in
    placeholder?: (item: T) => JSX.Element;                            // Placeholder during drag
}

// For component usage - extends base props with required children
type SortableComponentProps<T> = SortableProps<T> & {
    children?: (item: T, index: Accessor<number>) => JSX.Element;
};

function SortableComponent<T>(props: SortableComponentProps<T>) {
    if (!props.children) {
        return null;
    }

    const renderItem = props.children;
    const ctx = new SortableContext<T>(props.items);
    ctx.onReorder = props.onReorder;

    return (
        <SortableContextJsx.Provider value={ctx}>
            <div class={styles['st-sortable']}>
                <For each={ctx.items()}>
                    {(item, index) => (
                        <Item
                            item={item}
                            index={index()}
                            key={props.itemKey?.(item) ?? index()}
                            disabled={props.disabled}
                            animation={props.animation}
                            animationDuration={props.animationDuration}
                        >
                            {renderItem(item, index)}
                        </Item>
                    )}
                </For>
            </div>
        </SortableContextJsx.Provider>
    );
}

SortableComponent.Item = Item;
SortableComponent.defaultProps = new SortableProps();

export default SortableComponent;